import type { PermItem } from '../types/Roles.types';

export interface PermGroup {
  key: string;
  header: PermItem | null;
  children: Array<PermItem | PermGroup>;
  childIndices?: number[];
}

export const DEFAULT_INTEGRATIONS: string[] = [
  'FloodStage',
  'RescAlertDevicesManualRoadClosure',
  'RescAlertDevicesTechnologyBridge_FlashingLights',
  'RescAlertDevicesTechnologyBridge_Siren-GiantVoice',
  'ExacqVisionOverwatchVMS',
  'USGSMonitoringStation',
  'RescAlertDevicesBuoyConditions',
];

// Build index-based groups for a set of perms (handles linked headers).
function buildIndexGroups(items: PermItem[]): PermGroup[] {
  const idxMap = new Map<number, PermItem>();
  items.forEach((it) => idxMap.set(it.index as number, it));
  const sortedIdxs = Array.from(idxMap.keys()).sort((a, b) => a - b);

  const buildNodes = (
    startPtr: number,
    rangeEnd: number,
  ): [Array<PermItem | PermGroup>, number] => {
    const nodes: Array<PermItem | PermGroup> = [];
    let p = startPtr;
    while (p < sortedIdxs.length) {
      const idx = sortedIdxs[p];
      if (idx > rangeEnd) break;
      const item = idxMap.get(idx);
      if (!item) {
        p += 1;
        continue;
      }
      const linked = item.linked ?? 0;
      if (linked > 0) {
        const [children, newP] = buildNodes(p + 1, idx + linked);
        nodes.push({ key: `group-${idx}-${item.name}`, header: item, children });
        p = newP;
      } else {
        nodes.push(item);
        p += 1;
      }
    }
    return [nodes, p];
  };

  const [nodes] = buildNodes(0, Number.POSITIVE_INFINITY);

  // Compress consecutive top-level leaves into standalone groups.
  const result: PermGroup[] = [];
  let current: PermGroup | null = null;
  nodes.forEach((n) => {
    if ('header' in n) {
      if (current) {
        result.push(current);
        current = null;
      }
      result.push(n);
    } else {
      if (!current) current = { key: `standalone-${n.index}-${n.name}`, header: null, children: [] };
      current.children.push(n);
    }
  });
  if (current) result.push(current);
  return result;
}

/**
 * Group perms into the selected integrations (by token) plus the remainder grouped
 * using index+linked rules. Returns PermGroup[] in original order.
 */
export function buildPermGroups(
  perms: PermItem[],
  integrations: string[] = DEFAULT_INTEGRATIONS,
): PermGroup[] {
  if (!perms || perms.length === 0) return [];

  // Ensure each item has a numeric index (PermItem.index should exist from API)
  const withIdx = perms.map((p, i) => ({ ...p, index: typeof p.index === 'number' ? p.index : i }));

  // Collect all leaf perm indices under a node (PermItem or PermGroup).
  const collectIndices = (node: PermItem | PermGroup, out: number[]) => {
    if ('children' in node) node.children.forEach((c) => collectIndices(c, out));
    else if (typeof node.index === 'number') out.push(node.index);
  };

  const withChildIndices = (group: PermGroup): PermGroup => {
    const childIdxs: number[] = [];
    group.children.forEach((c) => collectIndices(c, childIdxs));
    if (childIdxs.length) group.childIndices = childIdxs;
    return group;
  };

  // Smallest index a group covers (header or any leaf), used for ordering.
  const firstIndexOf = (g: PermGroup): number => {
    const idxs: number[] = [];
    if (g.header && typeof g.header.index === 'number') idxs.push(g.header.index);
    if (g.childIndices) idxs.push(...g.childIndices);
    return idxs.length ? Math.min(...idxs) : Number.POSITIVE_INFINITY;
  };

  const byIndex = new Map<number, PermItem>(withIdx.map((p) => [p.index as number, p]));
  const assigned = new Set<number>();
  const integrationGroups: PermGroup[] = [];

  // Each integration is a master switch that owns the next `linked` perms by
  // index. Membership comes straight from that contiguous block (as provided
  // by the API) so unrelated perms are never pulled in by name similarity.
  // Names are normalized (lowercase, alphanumeric only) because upstream
  // parsing spaces out camelCase and swaps `-`/`_`, so raw tokens like
  // `FloodStage` must still match a parsed `Flood Stage`.
  const normalizeName = (s: string) => (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');

  for (const token of integrations) {
    const target = normalizeName(token);
    const master =
      withIdx.find((p) => normalizeName(p.name) === target && (p.linked ?? 0) > 0) ||
      withIdx.find((p) => normalizeName(p.name).includes(target) && (p.linked ?? 0) > 0);
    if (!master) continue;

    const masterIdx = master.index as number;
    if (assigned.has(masterIdx)) continue;
    assigned.add(masterIdx);

    const children: PermItem[] = [];
    for (let i = 1; i <= (master.linked ?? 0); i++) {
      const childIdx = masterIdx + i;
      const child = byIndex.get(childIdx);
      if (!child || assigned.has(childIdx)) continue;
      assigned.add(childIdx);
      children.push(child);
    }

    integrationGroups.push(
      withChildIndices({ key: `integration-${token}`, header: master, children }),
    );
  }

  // Remaining permissions grouped using index+linked rules.
  const remaining = withIdx.filter((p) => !assigned.has(p.index as number));
  const remainderGroups = buildIndexGroups(remaining).map(withChildIndices);

  return [...integrationGroups, ...remainderGroups].sort((a, b) => firstIndexOf(a) - firstIndexOf(b));
}
